import { claimAudioFocus, onOtherAudioFocus } from './focus'
/**
 * Wires the audio engine to the player store, MediaSession, and the server.
 *
 * Mounted once at the app root, *outside* the router, so navigation never
 * unmounts it and playback survives every route change.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { api, beacon } from '@/api/client'
import { isIncognito } from '@/lib/incognito'
import type { PlayerStatePayload, PlayerStateResponse } from '@/api/types'

import { engine } from './engine'
import { registerMediaHandlers, setMediaMetadata, setPlaybackState, setPositionState } from './mediaSession'
import { toQueueItem, usePlayer, type PlaySource } from './store'

/** A play counts after this much *actual playback*, matching the usual convention. */
const PLAY_COUNT_THRESHOLD_SECONDS = 30

/** A jump larger than this is a seek, not elapsed time, and is not credited to
 *  the listener. Timeupdate fires about four times a second. */
const MAX_CREDITED_STEP_SECONDS = 2

/** Close enough to the end to call it finished. The `ended` event resets the
 *  clock before the store advances, and a track can end a hair short of its
 *  declared duration, so the last few seconds are treated as the end. */
const COMPLETION_SLACK_SECONDS = 5

/** One listen in progress, from the moment it counts until the listener moves on. */
interface OpenListen {
  /** Queue entry uid, not the library item: the same song can appear twice. */
  uid: string
  /** Sent with both reports so the server lands them on one row. */
  key: string
  libraryItemId: number
  /** The furthest point actually reached. Tracked rather than read at the end,
   *  because by the time a track has ended its currentTime is already back at 0. */
  furthestSeconds: number
  durationSeconds: number
  /** Captured when the listen opens, not when it closes: starting a new queue is
   *  one of the ways a listen ends, and it would otherwise be attributed to
   *  wherever the listener went next. */
  source: PlaySource
  /**
   * Started during an incognito session, in which case nothing is ever sent.
   *
   * Also captured at open, so a listen is private or not for its whole life. The
   * alternative -- deciding per report -- would let a switch flipped mid-track
   * leave half a listen on record, which is the one outcome this mode must never
   * produce.
   *
   * Not the same question as the file being ephemeral. That flag is never set on
   * a track already in the library, so an owned track played privately looked
   * exactly like an ordinary play and was written down as one.
   */
  private: boolean
}

/** A fresh id per listen. `crypto.randomUUID` needs a secure context and Safari
 *  older than 15.4, which this app still runs on, does not have it at all. */
function newListenKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const listenRef = useRef<OpenListen | null>(null)
  /** Set by the engine's `ended` event, cleared by the play subscription. */
  const endedRef = useRef(false)
  const watchRef = useRef<{ uid: string; played: number; lastTime: number } | null>(null)
  const lastPersistRef = useRef(0)

  useEffect(() => onOtherAudioFocus(() => engine.pause()), [])

  // --- restore the previous session ------------------------------------
  // Runs once. Fails silently when signed out — /player/state 403s, and a
  // visitor on the login screen has no session to restore.
  useEffect(() => {
    let cancelled = false
    void api
      .get<PlayerStateResponse>('/player/state')
      .then((state) => {
        if (cancelled || usePlayer.getState().queue.length > 0) return
        const available = new Set((state.queue_items ?? []).map((item) => item.id))
        const entries = state.queue.filter((entry) => available.has(entry.library_item_id))
        const queue = (state.queue_items ?? []).map((item, index) => {
          const queued = toQueueItem(item)
          return queued ? { ...queued, suggested: entries[index]?.suggested === true } : null
        }).filter((i) => i !== null)
        usePlayer.getState().hydrate({
          queue,
          index: state.queue_index,
          positionMs: state.position_ms,
          shuffle: state.shuffle,
          repeat: state.repeat,
          volume: state.volume,
          source: state.source,
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // --- engine events -> store ------------------------------------------
  useEffect(() => {
    return engine.subscribe((event) => {
      const store = usePlayer.getState()
      switch (event.type) {
        case 'timeupdate':
          store._sync({ currentTime: event.currentTime, duration: event.duration })
          break
        case 'durationchange':
          store._sync({ duration: event.duration })
          break
        case 'play':
          claimAudioFocus()
          store._sync({ isPlaying: true, error: null })
          break
        case 'pause':
          store._sync({ isPlaying: false })
          break
        case 'waiting':
          store._sync({ isBuffering: true })
          break
        case 'playing':
          store._sync({ isBuffering: false })
          break
        case 'progress':
          store._sync({ buffered: event.buffered })
          break
        case 'ended':
          // A preview ending is not a track ending. Advancing here would skip
          // the song the user was actually listening to.
          if (store.preview) {
            store.stopPreview()
          } else {
            // Flagged before advancing, because advancing is not what tells the
            // play recorder a track finished. It infers that from the queue
            // entry changing — and at the end of the queue `next` pauses without
            // changing it, so the last track of every session sat with its
            // listen open and was never reported as completed however completely
            // it had played. Same for repeat 'one', which does not advance at all.
            endedRef.current = true
            store.next(true)
          }
          break
        case 'error':
          store._sync({ isPlaying: false, isBuffering: false, error: event.message })
          break
      }
    })
  }, [])

  // --- MediaSession action handlers ------------------------------------
  useEffect(() => {
    return registerMediaHandlers({
      play: () => usePlayer.getState().play(),
      pause: () => usePlayer.getState().pause(),
      previous: () => usePlayer.getState().previous(),
      next: () => usePlayer.getState().next(false),
      seekTo: (seconds) => usePlayer.getState().seek(seconds),
      stop: () => usePlayer.getState().pause(),
    })
  }, [])

  // --- MediaSession metadata, driven by the current track --------------
  useEffect(() => {
    return usePlayer.subscribe((state, previous) => {
      const current = state.queue[state.index] ?? null
      const before = previous.queue[previous.index] ?? null

      if (state.preview?.key !== previous.preview?.key) {
        // Show the clip on the lock screen while it plays, then hand the real
        // track's metadata back when it ends.
        setMediaMetadata(
          state.preview
            ? { title: state.preview.title, artistName: state.preview.artistName }
            : current,
        )
      } else if (current?.libraryItemId !== before?.libraryItemId) {
        setMediaMetadata(current)
      }
      if (state.isPlaying !== previous.isPlaying) {
        setPlaybackState(state.isPlaying)
      }
    })
  }, [])

  // --- position state, throttled ---------------------------------------
  useEffect(() => {
    const timer = window.setInterval(() => {
      const { isPlaying, currentTime, duration } = usePlayer.getState()
      if (isPlaying) setPositionState(currentTime, duration)
    }, 1000)
    return () => window.clearInterval(timer)
  }, [])

  // --- record plays -----------------------------------------------------
  //
  // A listen is reported twice: once when it passes the threshold, and once when
  // the listener moves on, carrying the same key and the final position. The
  // first call is what counts the play; the second is what makes the history
  // able to tell a song that was finished from one that was skipped — the single
  // most useful thing in it, and the thing a play counter cannot express.
  useEffect(() => {
    const report = (listen: OpenListen, completed: boolean, opening = false) => {
      // Nothing is sent at all, rather than sent and discarded: the strongest
      // form of "this left no trace" is a request that was never made.
      if (listen.private) return
      void api
        .post(`/library/tracks/${listen.libraryItemId}/played`, {
          event_key: listen.key,
          // Only the opening report may create a row. Saying so is what stops a
          // listen the server refused at the start from being written down at the
          // end, if whatever made it refuse stopped being true in between.
          opening,
          position_ms: Math.round(listen.furthestSeconds * 1000),
          duration_ms: listen.durationSeconds > 0
            ? Math.round(listen.durationSeconds * 1000)
            : null,
          completed,
          source: listen.source,
          // The real value, not a hardcoded false. These reports are only sent
          // for a listen that is not private — but if that ever stops being
          // true, the server should still refuse rather than be told it is fine.
          incognito: listen.private,
        })
        .then(() => {
          // Home and listening history update as soon as a listen is recorded.
          void queryClient.invalidateQueries({ queryKey: ['history'] })
          if (opening) void queryClient.invalidateQueries({ queryKey: ['daylists', 'today'] })
        })
        .catch(() => {
          /* a lost play is not worth interrupting playback for */
        })
    }

    const close = () => {
      const listen = listenRef.current
      if (!listen) return
      listenRef.current = null
      const finished =
        listen.durationSeconds > 0 &&
        listen.furthestSeconds >= listen.durationSeconds - COMPLETION_SLACK_SECONDS
      report(listen, finished)
    }

    return usePlayer.subscribe((state) => {
      // Auditioning a clip is not listening to a song. The open listen is left
      // alone rather than closed: the preview borrows the audio element and hands
      // it straight back, and the track underneath was never departed from.
      if (state.preview) return

      const current = state.queue[state.index]

      // The track ran out. Close on that rather than waiting for the queue entry
      // to change, which at the end of a queue never happens.
      if (endedRef.current) {
        endedRef.current = false
        close()
        // A repeat starts a fresh listen and has to earn it again, rather than
        // inheriting the playback this one was already credited with.
        watchRef.current = null
      }

      const open = listenRef.current

      // Moved on — to another track, or to nothing at all.
      if (open && open.uid !== current?.uid) close()
      if (!current) return

      // An open listen is also the record that this play has been counted, so
      // there is nothing else to do but keep its high-water mark current — that
      // is what the final report is built from. Keying on the queue entry rather
      // than the track means the same song twice in one queue is two listens.
      const listen = listenRef.current
      if (listen) {
        listen.furthestSeconds = Math.max(listen.furthestSeconds, state.currentTime)
        // A duration shorter than the point already reached is not this track's.
        // Ending a preview hands the element back with the clip's thirty seconds
        // still on the clock, and adopting that marked a song you were two
        // minutes into as finished.
        if (state.duration > 0 && state.duration >= listen.furthestSeconds) {
          listen.durationSeconds = state.duration
        }
        return
      }

      // Time actually spent listening, accumulated from the clock's forward
      // steps — not the clock itself.
      //
      // Reading `currentTime >= 30` treated the position as a proxy for elapsed
      // listening, and it is not one. Restoring a session parked at 1:26 cleared
      // the threshold the instant the page loaded, so a paused track nobody had
      // touched was logged as a play. Seeking forward did the same thing. Steps
      // are credited only while playing and only when small enough to be the
      // clock ticking rather than a jump.
      let watch = watchRef.current
      if (!watch || watch.uid !== current.uid) {
        watch = { uid: current.uid, played: 0, lastTime: state.currentTime }
        watchRef.current = watch
      }
      const step = state.currentTime - watch.lastTime
      watch.lastTime = state.currentTime
      if (state.isPlaying && step > 0 && step < MAX_CREDITED_STEP_SECONDS) {
        watch.played += step
      }

      const longEnough = watch.played >= PLAY_COUNT_THRESHOLD_SECONDS
      const shortTrack = state.duration > 0 && watch.played >= state.duration * 0.5
      if (!longEnough && !shortTrack) return

      const opened: OpenListen = {
        uid: current.uid,
        key: newListenKey(),
        libraryItemId: current.libraryItemId,
        furthestSeconds: state.currentTime,
        durationSeconds: state.duration,
        source: current.suggested ? 'queue' : state.source,
        private: isIncognito(),
      }
      listenRef.current = opened
      report(opened, false, true)
    })
  }, [queryClient])

  // --- flush an open listen when the page goes away ---------------------
  //
  // Without this, closing the tab or reloading leaves the listen's first report
  // standing on its own: position frozen at thirty seconds, `completed` false
  // forever. That is indistinguishable from a skip, and it is not a rare case —
  // reloading a music player mid-track is an ordinary thing to do.
  //
  // It reports the position and never claims completion, because at this moment
  // nobody knows how the track would have ended. The server merges both fields
  // forwards, so a real closing report afterwards still wins.
  //
  // `pagehide` covers unload and the back/forward cache. `visibilitychange` is
  // there for iOS, where pagehide is unreliable — but backgrounding a music app
  // is not stopping it, so the listen stays open and only the position is sent.
  useEffect(() => {
    const flush = () => {
      const listen = listenRef.current
      if (!listen || listen.private) return
      const { currentTime, duration } = usePlayer.getState()
      listen.furthestSeconds = Math.max(listen.furthestSeconds, currentTime)
      if (duration > 0) listen.durationSeconds = duration
      beacon(`/library/tracks/${listen.libraryItemId}/played`, {
        event_key: listen.key,
        position_ms: Math.round(listen.furthestSeconds * 1000),
        duration_ms: listen.durationSeconds > 0 ? Math.round(listen.durationSeconds * 1000) : null,
        completed: false,
        source: listen.source,
        incognito: listen.private,
        opening: false,
      })
    }
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onHidden)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onHidden)
    }
  }, [])

  // --- persist the queue so a reload or another device resumes ----------
  useEffect(() => {
    return usePlayer.subscribe((state, previous) => {
      // Reference equality, not length: the store replaces the array on every
      // queue change, so this catches reorders too. Comparing lengths missed a
      // re-shuffle — same tracks, same count, same index — which then went
      // unsaved for up to ten seconds and resumed in the old order.
      // While a preview borrows the audio element, currentTime belongs to the
      // clip. Persisting it would overwrite where the user actually was, on
      // every device that restores this queue.
      if (state.preview) return

      const queueChanged =
        state.index !== previous.index ||
        state.queue !== previous.queue ||
        state.shuffle !== previous.shuffle ||
        state.repeat !== previous.repeat

      const now = Date.now()
      // Position moves every 250ms; only persist it occasionally.
      const positionDue = now - lastPersistRef.current > 10_000

      if (!queueChanged && !positionDue) return
      lastPersistRef.current = now

      const payload: PlayerStatePayload = {
        queue: state.queue.map((item) => ({
          library_item_id: item.libraryItemId,
          track_id: item.trackId,
          suggested: item.suggested === true,
        })),
        queue_index: Math.max(0, state.index),
        position_ms: Math.round(state.currentTime * 1000),
        shuffle: state.shuffle,
        repeat: state.repeat,
        volume: state.volume,
        source: state.source,
      }
      void api.put('/player/state', payload).catch(() => {
        /* offline is fine; local state is the source of truth for this session */
      })
    })
  }, [])

  // --- pause on sign-out ------------------------------------------------
  useEffect(() => {
    const onUnload = () => engine.pause()
    window.addEventListener('pagehide', onUnload)
    return () => window.removeEventListener('pagehide', onUnload)
  }, [])

  return <>{children}</>
}
